/* ss-image-warm.js — a picture is ready before the shopper reaches it.
 *
 * Native loading="lazy" leaves the fetch distance to the browser. Chrome starts
 * about 1250px ahead on a good connection; Safari waits until the image is all
 * but on screen. Measured on live at a reading pace in WebKit (390px wide,
 * 2026-09-16): 23 images across nine pages were watched loading, ten of them
 * collection cards, four the product thumbnail strip. At a flick pace on 4G in
 * Chrome, 16.
 *
 * Two passes, both leaving the first paint alone, both starting once the page
 * has settled (see settled() below: `load`, or SOON ms into the visit once the
 * first screen's pictures have painted, whichever is first):
 *   1. near: lazy images within 2.5 viewports fetch at low fetch priority, so
 *      even a fast flick meets a decoded picture. (A 2.5-viewport band from the
 *      start cost the homepage LCP 1.7s -> 3.4s on a 4G phone, seven extra
 *      images fetched before load; a one-viewport band before the fold had
 *      landed still cost 1.3s. Measured, then gated.)
 *   2. ahead: once settled, in idle time, lazy images down to twelve screens
 *      below the furthest the shopper has scrolled are warmed in document
 *      order three at a time, each batch waiting for the last, without
 *      contending with the hero, fonts or the cart. The reach grows as they
 *      scroll, and with their speed: four seconds of travel at the fastest
 *      pace measured so far, never less than twelve screens. (Until 2026-10-30
 *      this pass warmed the whole page: ~1.0 MB of pictures on a phone homepage
 *      within 5s of `load`, while PostHog had half of PL phone visits stopping
 *      within 1.6 screens on home and product pages, 4.2 on collections, at
 *      0.04-0.08 screens/s averaged over the visit, p99 0.9-1.8. Six screens
 *      is the first second of a 6 screens/s flick from rest: no reaction to
 *      its speed lands a picture on a 1.6 Mbps line before it gets there, so
 *      those must already be warm (four screens let two product-page pictures
 *      at 5.2 and 6.8 screens arrive late). Past that a flick reaches 24, the
 *      whole of most pages, as the whole-page sweep did; four seconds is the
 *      Jade queue's horizon too. Twelve, not six, since 2026-10-31: at six,
 *      a shopper who rested ten seconds on a long product page and then
 *      flicked met three pictures a run arriving 2.6 s late on a 1.6 Mbps line
 *      (inner-sanctum-attar, n=5); at twelve none did, for 15-173 KB less
 *      saved. Sho, 2026-10-31: the shopper's experience comes first.)
 * Flipping `loading` from lazy to eager resumes the element's own load steps
 * (spec: "will lazy load element"), so srcset/sizes selection is untouched and
 * the browser's cache, priorities and decoding behave as for any image.
 * Skipped entirely under Save-Data or a prerender; on a 2g-class connection
 * only the near band runs (Safari exposes neither signal, so iOS always gets
 * the full pass: after load, at low priority, which is the cheapest form of
 * it). Images inside a content-visibility:auto band that is skipped are not
 * intersecting for the observer; they are picked up by the sweep, which places
 * them by the band's own reserved box (see top()). A picture the page does not
 * draw at this viewport (display:none, or a collapsed box) is not swept: native
 * lazy loading, and the near band, fetch it if it ever appears. Images without a src/srcset (the menu's data-src
 * pattern, posters, <template> content) are not touched: their loaders own them.
 * A page that orders its own pictures (the Jade v2 page's queue, assets/jade-v2-core.js) marks them
 * [data-pics]: they are left to it. While it sets html[data-pics-busy] both passes wait for its
 * `ss:pics-idle` (or 20s): the header drawer's menu pictures (about 160 KB, within the near band in
 * layout) were measured sharing a 2 Mbps line with the next pictures on that page.
 */
(function () {
  'use strict';
  if (document.prerendering) return;
  var conn = navigator.connection || {};
  if (conn.saveData) return;
  var slow = /(^|[^3-9])2g/.test(conn.effectiveType || '');
  var SELECTOR = 'img[loading="lazy" i][src]:not([data-pics]), img[loading="lazy" i][srcset]:not([data-pics])';

  function warm(img) {
    if (String(img.getAttribute('loading')).toLowerCase() !== 'lazy') return false;
    if (!img.hasAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'low');
    img.setAttribute('loading', 'eager');
    return true;
  }
  function pending() { return document.querySelectorAll(SELECTOR); }
  // Counts a picture once, whichever of load/error comes: a listener left
  // behind would count it again on a later srcset or src swap and start a
  // second chain of batches beside the first.
  function settleOnce(img, done) {
    function once() { img.removeEventListener('load', once); img.removeEventListener('error', once); done(); }
    img.addEventListener('load', once);
    img.addEventListener('error', once);
  }

  var io = null;
  function band(margin) {
    if (!('IntersectionObserver' in window)) return null;
    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.unobserve(entries[i].target);
        warm(entries[i].target);
      }
    }, { rootMargin: margin });
    return io;
  }
  var mo = null, swept = false;
  function observeAll() {
    var list = pending();
    for (var i = 0; i < list.length; i++) io ? io.observe(list[i]) : warm(list[i]);
    if (!list.length && swept && mo) { mo.disconnect(); mo = null; }
  }
  // Nothing before the first screen has painted. A one-viewport band opened
  // after the fold's eager images had arrived once measured against the
  // product page's LCP on a 4G phone (1128ms -> 1772ms over 8 interleaved
  // loads, 2026-09-16): the gallery set its hero source from script then, so
  // "the fold is complete" fired on a placeholder and the band shared the
  // connection with the real hero. Every hero is in the markup now, with its
  // srcset; the first screen and its edge are covered by eager markup
  // (product thumbnails, hero), and every warm fetch waits for settled().
  function start() {
    whenFree(function () {
      band(slow ? '100% 0px' : '250% 0px');
      observeAll();
    });
  }
  // A page queue at work (html[data-pics-busy]) has the line: both passes wait for its ss:pics-idle, or 20s.
  function whenFree(fn) {
    if (!document.documentElement.hasAttribute('data-pics-busy')) return fn();
    var t = setTimeout(go, 20000);
    function go() { if (!t) return; clearTimeout(t); t = 0; fn(); }
    document.addEventListener('ss:pics-idle', go, { once: true });
  }
  // A hero video that is still buffering after `load` keeps the connection
  // too: warming waits for it to play (measured +200ms to first frame without
  // this, 8 loads), or 1.5s, whichever is first.
  function afterHeroVideo(fn) {
    var v = null;
    for (var i = 0; i < document.querySelectorAll('video[autoplay]').length; i++) {
      var c = document.querySelectorAll('video[autoplay]')[i];
      var r = c.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight && r.width > 0) { v = c; break; }
    }
    if (!v || v.readyState >= 3 || v.paused === false && v.currentTime > 0) return fn();
    var t = setTimeout(go, 1500);
    function go() { if (!fn) return; clearTimeout(t); var f = fn; fn = null; f(); }
    v.addEventListener('playing', go, { once: true });
    v.addEventListener('error', go, { once: true });
  }
  // SETTLED: `load`, or SOON ms into the visit once every picture on the first
  // screen has arrived and painted, whichever is first; then an idle moment.
  // `load` alone was a promise a third party could break: the Google & YouTube
  // app's web pixel injects gtag/js (GT-KV59PGQM) in a frame, and a frame holds
  // the page's `load`. In real Safari on a Mac it held `load` past 10 s in 3 of
  // 3 visits (once to 29 s); held 30 s in WebKit on good 4G, the homepage met
  // 6-10 late pictures a visit instead of 0-1 (2026-10-31), because nothing
  // here had started. SOON sits past `load` on every page measured in its
  // ordinary course (four pages, 4x CPU: 1.6 Mbps p90 7.0 s, 9 Mbps p90 3.3 s),
  // so an ordinary visit is unchanged: the fallback only cuts a stalled wait
  // short, and never before the hero has painted. 4 s was tried and lost: on a
  // 1.6 Mbps homepage it came before `load`, the story plates and this sweep
  // shared the line with the phone's hero film, and `load` went 4.3 -> 7.1 s
  // and the film's first frame 3.6 -> 9.2 s (2026-11-01, n=2, both runs).
  // At SOON the screen the shopper is looking at must be complete too: `load`
  // also waits for the pictures a scroll pulls in, and on a 1.6 Mbps line a
  // shopper reading from 3 s still had them arriving at 7 s; warming then took
  // the line from the picture on screen (a homepage story plate went 4.6 ->
  // 6.0 s blank, 2026-11-01, n=4 of 4). So the fallback waits for those first.
  // Both waits end at 2 x SOON at the latest.
  var SOON = 7000;
  var calm = document.readyState === 'complete', queue = [];
  function settled(fn) { if (calm) fn(); else queue.push(fn); }
  function settle() {
    if (calm) return;
    calm = true;
    for (var i = 0; i < queue.length; i++) queue[i]();
    queue = null;
  }
  // The screen as it is now: every picture with a source whose box meets the
  // viewport (a tracking pixel aside), until each has loaded or failed (asked
  // again once they have, as a scroll may have brought more); then two frames,
  // so they are on screen and not merely in memory. `until` (ms since the
  // visit began) stops the asking: a picture from a host that never answers
  // would otherwise hold the fallback as long as it holds `load`.
  function screenDone(fn, until) {
    var imgs = document.images, wait = 1, vh = window.innerHeight, vw = window.innerWidth, busy = false, cut = 0, over = false;
    var raf = window.requestAnimationFrame || function (f) { setTimeout(f, 16); };
    function one() {
      if (over || --wait > 0) return;
      over = true;
      clearTimeout(cut);
      if (calm) return;
      if (busy) return screenDone(fn, until);
      raf(function () { raf(fn); });
    }
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete || !(img.getAttribute('src') || img.getAttribute('srcset'))) continue;
      // Not drawn, or in a content-visibility band being skipped (off screen by
      // definition): asking for its box would lay the band out (see top()).
      if (img.checkVisibility && !img.checkVisibility({ contentVisibilityAuto: true })) continue;
      var r = img.getBoundingClientRect();
      if (r.width < 8 || r.height < 8 || r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) continue;
      wait++; busy = true;
      settleOnce(img, one);
    }
    if (busy && until) cut = setTimeout(function () { if (over) return; over = true; if (!calm) fn(); }, Math.max(0, until - performance.now()));
    one();
  }
  if (!calm) {
    window.addEventListener('load', settle, { once: true });
    screenDone(function () {
      var ric = window.requestIdleCallback || function (f) { setTimeout(f, 150); };
      setTimeout(function () { screenDone(function () { ric(settle, { timeout: 1000 }); }, 2 * SOON); }, Math.max(0, SOON - performance.now()));
    }, 2 * SOON);
  }
  settled(function () { afterHeroVideo(start); });

  // Markup that arrives later (drawer, menu panels, section rerenders) joins
  // the near band the same way. Coalesced so a burst of mutations scans once.
  // Disconnected once the sweep has run and nothing lazy is left, so a page
  // that keeps rerendering its cart drawer does not keep paying for scans;
  // and while the sweep is parked waiting for a scroll (most visits never
  // scroll far), reconnected with a rescan when it resumes.
  var DOM = { childList: true, subtree: true };
  if ('MutationObserver' in window) {
    var scan = 0;
    mo = new MutationObserver(function () {
      if (scan || !io) return;
      scan = setTimeout(function () { scan = 0; observeAll(); }, 200);
    });
    mo.observe(document.documentElement, DOM);
  }

  if (slow) return;
  var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 150); };
  // How far down the sweep goes, past the bottom of the furthest screen the
  // shopper has seen: twelve screens, or four seconds at their fastest pace
  // (screens/s, sampled at least 250ms apart so one swipe of a reader is not
  // read as a flick). Scrolling back up or slowing down never shrinks it.
  var AHEAD = 12, LEAD = 4, furthest = 0, ahead = AHEAD, ly = -1, lt = 0, parked = false, resume = 0, wake = 0;
  // Where a picture sits, in page px (sy: the scroll offset, read once a batch).
  // Inside a band that content-visibility:auto is skipping, the band's own
  // reserved box stands in for it: asking for the picture's box makes Chromium
  // lay the skipped band out to answer (~4ms a band on a desktop CPU, again
  // for every pending picture in every batch: 160-390ms of main thread a page
  // on a 4x-throttled phone, long frames up to 95ms, measured 2026-10-31).
  // checkVisibility answers from style alone. A picture without a box of its
  // own (a browser without checkVisibility) is placed by its nearest ancestor
  // that has one.
  // NaN: a picture this viewport does not draw, which the sweep leaves alone.
  // display:none (the phone carousel and the phone bar on a desktop, a review
  // behind "show all", a gift choice not yet offered), or a box collapsed to
  // nothing though the markup gives it a width and height. On the-sanctum in
  // WebKit the pairing band's figure collapses to 0x0 at 390px, and the sweep
  // fetched its photograph, the hero's picture again at 1200w (293 KB), right
  // after `load`, for a box nobody sees (2026-10-31). If it ever appears, the
  // near band and native lazy loading fetch it as for any picture.
  function top(img, sy) {
    var el = img;
    if (el.checkVisibility) {
      if (!el.checkVisibility()) return NaN;
      while (el.parentElement && !el.checkVisibility({ contentVisibilityAuto: true })) el = el.parentElement;
    }
    if (el === img) {
      var b = img.getBoundingClientRect();
      if (b.width && b.height) return b.top + sy;
      if (img.getAttribute('width') && img.getAttribute('height')) return NaN;
    }
    for (; el && el.nodeType === 1; el = el.parentElement) {
      var r = el.getBoundingClientRect();
      if (r.width || r.height) return r.top + sy;
    }
    return 0;
  }

  function batch() {
    var list = pending(), sy = window.scrollY || window.pageYOffset || 0, open = 0, n = 0, next = Infinity;
    furthest = Math.max(furthest, sy);
    var lim = furthest + window.innerHeight * (1 + ahead);
    var done = function () { if (--open <= 0) idle(batch, { timeout: 1500 }); };
    if (!list.length) { swept = true; observeAll(); return; }
    for (var i = 0; i < list.length && n < 3; i++) {
      var img = list[i], y = top(img, sy);
      if (y !== y) continue;
      if (y > lim) { next = Math.min(next, y); continue; }
      if (!warm(img)) continue;
      n++; open++;
      if (img.complete) { open--; continue; }
      settleOnce(img, done);
    }
    // Nothing left within reach: park until the reach gets to the next
    // picture, or has grown a screen (the page may have shifted, or new
    // markup arrived). Resuming on every scroll pause instead re-queried the
    // page each time: 376 of 604ms of the script's main thread on a product
    // page read at 300px/s on a 4x-throttled phone (2026-10-31).
    if (!n) { parked = true; wake = Math.min(next, lim + window.innerHeight); if (mo) mo.disconnect(); return; }
    if (open <= 0) idle(batch, { timeout: 1500 });
  }
  // The scroll is read at most every 100ms, not on every scroll event: each
  // read of scrollY makes the browser finish any pending layout first, and on
  // the homepage per-event reads were 135 of the script's 165ms of main
  // thread at 1200px/s on a 4x-throttled phone (2026-10-31). The last event
  // of a scroll is always read, so the furthest point is never missed.
  var tq = 0;
  function sample() {
    tq = 0;
    var y = window.scrollY || window.pageYOffset || 0, t = Date.now();
    furthest = Math.max(furthest, y);
    if (ly < 0 || t - lt > 1000) { ly = y; lt = t; }
    else if (t - lt >= 250) {
      ahead = Math.max(ahead, Math.abs(y - ly) / window.innerHeight / (t - lt) * 1000 * LEAD);
      ly = y; lt = t;
    }
    if (!parked || resume || furthest + window.innerHeight * (1 + ahead) < wake) return;
    resume = setTimeout(function () {
      resume = 0; parked = false;
      if (mo) { mo.observe(document.documentElement, DOM); observeAll(); }
      idle(batch, { timeout: 500 });
    }, 50);
  }
  window.addEventListener('scroll', function () { if (!tq) tq = setTimeout(sample, 100); }, { passive: true });
  function sweepWhenFree() { whenFree(batch); }
  settled(function () { afterHeroVideo(function () { idle(sweepWhenFree, { timeout: 3000 }); }); });
})();
